import { RootState } from "@/store/dtm";
import { propertyInfoType } from "@/types/propertyInfoType";
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export interface propertyInfoState {
    propertyInfo: propertyInfoType
}

const initialState: propertyInfoState = {
    propertyInfo: {
        purchasePrice: 0,
        propertyValue: 0,
        originalPurchasePrice: 0,
        mortgageBalance: 0,
        mortgageHolder: "",
        propertyTax: 0,
        livableArea: 0,
        livableAreaUnit: "",
        heatingCost: 0,
        heatingType: "",
        maintenanceFee: 0,
        purposeOfProperty: "",
        rentOut: false,
        separateUnit: false,
        monthlyRent: 0,
        subjectPropertyRentalUnits: 0,
        ownOtherRentalProperties: false,
        numOfRentalProperties: 0,
        numOfRentalUnits: 0,
        ownershipPercentage: undefined,
        closingDate: "",
        fundingDate: "",
        financingConditionExists: "",
        financingConditionDate: "",
    }
}


export const propertyInfoSlice = createSlice({
    name: "propertyInfo",
    initialState,
    reducers: {
        setPurchasePrice: (state, action: PayloadAction<number>) => {
            const price = action.payload;
            state.propertyInfo.purchasePrice = price;
        },
        setPropertyValue: (state, action: PayloadAction<number>) => {
            const value = action.payload;
            state.propertyInfo.propertyValue = value;
        },
        setOriginalPurchasePrice: (state, action: PayloadAction<number>) => {
            const value = action.payload;
            state.propertyInfo.originalPurchasePrice = value;
        },
        setMortgageBalance: (state, action: PayloadAction<number>) => {
            const balance = action.payload;
            state.propertyInfo.mortgageBalance = balance;
        },
        setMortgageHolder: (state, action: PayloadAction<string>) => {
            const holder = action.payload;
            state.propertyInfo.mortgageHolder = holder;
        },
        setPropertyTax: (state, action: PayloadAction<number>) => {
            const tax = action.payload;
            state.propertyInfo.propertyTax = tax;
        },
        setLivableArea: (state, action: PayloadAction<number>) => {
            const area = action.payload;
            state.propertyInfo.livableArea = area;
        },
        setLivableAreaUnit: (state, action: PayloadAction<string>) => {
            const unit = action.payload;
            state.propertyInfo.livableAreaUnit = unit;
        },
        setHeatingCost: (state, action: PayloadAction<number>) => {
            const cost = action.payload;
            state.propertyInfo.heatingCost = cost;
        },
        setHeatingType: (state, action: PayloadAction<string>) => {
            const type = action.payload;
            state.propertyInfo.heatingType = type;
        },
        setMaintenanceFee: (state, action: PayloadAction<number>) => {
            const fee = action.payload;
            state.propertyInfo.maintenanceFee = fee;
        },
        setPurposeOfProperty: (state, action: PayloadAction<string>) => {
            const purpose = action.payload;
            state.propertyInfo.purposeOfProperty = purpose;
        },
        setRentOut: (state, action: PayloadAction<boolean>) => {
            const rentOut = action.payload;
            state.propertyInfo.rentOut = rentOut;
        },
        setSubjectPropertyRentalUnits: (state, action: PayloadAction<number>) => {
            const qty = action.payload;
            state.propertyInfo.subjectPropertyRentalUnits = qty;
        },
        setSeparateUnit: (state, action: PayloadAction<boolean>) => {
            const separate = action.payload;
            state.propertyInfo.separateUnit = separate;
        },
        setMonthlyRent: (state, action: PayloadAction<number>) => {
            const rent = action.payload;
            state.propertyInfo.monthlyRent = rent;
        },
        setOwnOtherRentalProperties: (state, action: PayloadAction<boolean>) => {
            const ownOther = action.payload;
            state.propertyInfo.ownOtherRentalProperties = ownOther;
        },
        setNumOfRentalProperties: (state, action: PayloadAction<number>) => {
            const qty = action.payload;
            state.propertyInfo.numOfRentalProperties = qty;
        },
        setNumOfRentalUnits: (state, action: PayloadAction<number>) => {
            const qty = action.payload;
            state.propertyInfo.numOfRentalUnits = qty;
        },
        setOwnershipPercentage: (state, action: PayloadAction<number | undefined>) => {
            const percentage = action.payload;
            state.propertyInfo.ownershipPercentage = percentage;
        },
        setClosingDate: (state, action: PayloadAction<string>) => {
            const date = action.payload;
            state.propertyInfo.closingDate = date;
        },
        setFundingDate: (state, action: PayloadAction<string>) => {
            const date = action.payload;
            state.propertyInfo.fundingDate = date;
        },
        setFinancingConditionExists: (state, action: PayloadAction<string>) => {
            const exists = action.payload;
            state.propertyInfo.financingConditionExists = exists;
        },
        setFinancingConditionDate: (state, action: PayloadAction<string>) => {
            const date = action.payload;
            state.propertyInfo.financingConditionDate = date;
        },
        clearPropertyInfoState: (state) => {
            state.propertyInfo.purchasePrice = 0;
            state.propertyInfo.propertyValue = 0;
            state.propertyInfo.originalPurchasePrice = 0;
            state.propertyInfo.mortgageBalance = 0;
            state.propertyInfo.mortgageHolder = "";
            state.propertyInfo.propertyTax = 0;
            state.propertyInfo.livableArea = 0;
            state.propertyInfo.livableAreaUnit = "";
            state.propertyInfo.heatingCost = 0;
            state.propertyInfo.heatingType = "";
            state.propertyInfo.maintenanceFee = 0;
            state.propertyInfo.purposeOfProperty = "";
            state.propertyInfo.rentOut = false;
            state.propertyInfo.subjectPropertyRentalUnits = 0;
            state.propertyInfo.separateUnit = false;
            state.propertyInfo.monthlyRent = 0;
            state.propertyInfo.ownOtherRentalProperties = false;
            state.propertyInfo.numOfRentalProperties = 0;
            state.propertyInfo.numOfRentalUnits = 0;
            state.propertyInfo.ownershipPercentage = undefined;
            state.propertyInfo.closingDate = "";
            state.propertyInfo.fundingDate = "";
            state.propertyInfo.financingConditionExists = "";
            state.propertyInfo.financingConditionDate = "";
        }
    }
});

export const { 
    setPurchasePrice,
    setPropertyValue,
    setOriginalPurchasePrice,
    setMortgageBalance,
    setMortgageHolder,
    setPropertyTax, 
    setLivableArea,
    setLivableAreaUnit,
    setHeatingCost,
    setHeatingType,
    setMaintenanceFee,
    setPurposeOfProperty,
    setRentOut,
    setSubjectPropertyRentalUnits,
    setSeparateUnit,
    setMonthlyRent,
    setOwnOtherRentalProperties,
    setNumOfRentalProperties,
    setNumOfRentalUnits,
    setOwnershipPercentage,
    setClosingDate,
    setFundingDate,
    setFinancingConditionExists,
    setFinancingConditionDate,
    clearPropertyInfoState 
} = propertyInfoSlice.actions

export const selectPropertyInfo = (state: RootState) => state.propertyInfo;

export default propertyInfoSlice.reducer